import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { SectorService } from '../../../../core/services/sector.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Sector } from '../../../../core/models/sector';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-sector-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './sector-form.component.html',
  styleUrl: './sector-form.component.scss',
  providers: [MessageService],
})
export class SectorFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};
  originalSector!: Sector;
  edited: boolean = false;
  constructor(
    private fb: FormBuilder,
    private sectorService: SectorService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();

    this.form = this.fb.group({
      id: [null],
      name: [null, Validators.compose([
        Validators.required,
         Validators.pattern(RegexConstants.SECTORS_NAME)])],
      nameEs: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.SECTORS_NAME_ES)])],
      nameAr: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.SECTORS_NAME_AR)])],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalSector = JSON.parse(JSON.stringify(this.config.data));
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
    this.form.valueChanges.subscribe(() => {
      this.edited = true;
    });
  }

  loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.sector.SectorValidators.required.nameEn',
        'layout.configurations.sector.SectorValidators.required.nameEs',
        'layout.configurations.sector.SectorValidators.required.nameAr',
        'layout.configurations.sector.SectorValidators.pattern.nameEn',
        'layout.configurations.sector.SectorValidators.pattern.nameEs',
        'layout.configurations.sector.SectorValidators.pattern.nameAr',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            name: translations['layout.configurations.sector.SectorValidators.required.nameEn'],
            nameEs: translations['layout.configurations.sector.SectorValidators.required.nameEs'],
            nameAr: translations['layout.configurations.sector.SectorValidators.required.nameAr'],
          },
          pattern: {
            name: translations['layout.configurations.sector.SectorValidators.pattern.nameEn'],
            nameEs: translations['layout.configurations.sector.SectorValidators.pattern.nameEs'],
            nameAr: translations['layout.configurations.sector.SectorValidators.pattern.nameAr'],
          },
        };
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.sector.editSector.errorMessage.title',
        'layout.configurations.sector.editSector.errorMessage.alreadyExists',
        'layout.configurations.sector.addSector.errorMessage.title',
        'layout.configurations.sector.addSector.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(sector: Sector) {
    this.form.patchValue({
      id: sector.id,
      name: sector.name,
      nameEs: sector.nameEs,
      nameAr: sector.nameAr,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateSector();
    } else {
      this.addSector();
    }
  }

  addSector() {
    this.sectorService.addSector(this.form.value).subscribe({
      next: (sector: Sector) => {
        this.ref.close(sector);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the sector: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.sector.addSector.errorMessage.title'],
          detail: error.error?.message || this.translations['layout.configurations.sector.addSector.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateSector() {
    const updatedSector = this.form.value;
    const isChanged = JSON.stringify(this.originalSector) !== JSON.stringify(updatedSector);
    if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }

    this.sectorService.updateSector(updatedSector).subscribe({
      next: (sector: Sector) => {
        this.ref.close({ type: 'updated', data: sector });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the sector: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.sector.editSector.errorMessage.title'],
          detail: this.translations['layout.configurations.sector.editSector.errorMessage.alreadyExists'],
        });
      },
    });
  }
  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required'] ? this.errorMessages.required[key] 
          : control.errors['minlength']
          ? this.errorMessages.minLength[key]
          : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
